import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'

import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'

import RouteViewComponent from '../layouts/RouterBypass.vue'
import { ecommerceRoutes } from './ecommerce-routes'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'home' },
  },
  {
    name: 'home',
    path: '/',
    component: () => import('../pages/home/HomePage.vue'),
  },
  {
    name: 'settings',
    path: '/settings',
    component: () => import('../pages/settings/Settings.vue'),
  },
  // Removed standalone settings/appearance route - using the nested one under /admin/settings instead
  // eCommerce routes
  ...ecommerceRoutes,
  {
    name: 'admin',
    path: '/admin',
    component: AppLayout,
    redirect: { name: 'dashboard' },
    children: [
      {
        name: 'dashboard',
        path: 'dashboard',
        component: () => import('../pages/admin/dashboard/Dashboard.vue'),
      },
      {
        name: 'users-management',
        path: 'users',
        component: RouteViewComponent,
        children: [
          {
            name: 'user-list',
            path: 'user-list',
            component: () => import('../pages/users/UserList.vue'),
          },
          {
            name: 'user-roles',
            path: 'user-roles',
            component: () => import('../pages/users/UserRoles.vue'),
          },
          {
            name: 'user-permissions',
            path: 'user-permissions',
            component: () => import('../pages/users/UserPermissions.vue'),
          },
        ],
      },
      {
        name: 'iot-service',
        path: 'iot-core',
        component: () => import('../pages/services/IoTCore.vue'),
        children: [
          {
            name: 'iot-core-index',
            path: '',
            component: () => import('../pages/services/IoTCoreOverview.vue'),
          },
          {
            name: 'devices',
            path: 'devices',
            component: RouteViewComponent,
            children: [
              {
                name: 'device-list',
                path: 'device-list',
                component: () => import('../pages/devices/DeviceList.vue'),
              },
              {
                name: 'device-manage',
                path: 'manage/:id',
                component: () => import('../pages/devices/DeviceManage.vue'),
              },
              {
                name: 'add-device',
                path: 'add-device',
                component: () => import('../pages/devices/AddDevice.vue'),
              },
              {
                name: 'device-groups',
                path: 'device-groups',
                component: () => import('../pages/devices/DeviceGroups.vue'),
              },
              {
                name: 'device-logs',
                path: 'device-logs',
                component: () => import('../pages/devices/DeviceLogs.vue'),
              },
            ],
          },
          {
            name: 'automation',
            path: 'automation',
            component: RouteViewComponent,
            children: [
              {
                name: 'scenes',
                path: 'scenes',
                component: () => import('../pages/automation/Scenes.vue'),
              },
              {
                name: 'automations',
                path: 'automations',
                component: () => import('../pages/automation/Automations.vue'),
              },
              {
                name: 'schedules',
                path: 'schedules',
                component: () => import('../pages/automation/Schedules.vue'),
              },
            ],
          },
        ],
      },
      {
        name: 'dashboard-api-test',
        path: 'dashboard-api-test',
        component: () => import('../pages/admin/dashboard/DashboardApiTest.vue'),
      },
      {
        name: 'api-usage-details',
        path: 'dashboard/api-usage-details',
        component: () => import('../pages/admin/dashboard/ApiUsageDetails.vue'),
      },
      {
        name: 'developer',
        path: 'developer',
        component: RouteViewComponent,
        children: [
          {
            name: 'apps-list',
            path: 'apps-list',
            component: () => import('../pages/developer/AppsList.vue'),
          },
          {
            name: 'webhooks',
            path: 'webhooks',
            component: () => import('../pages/developer/Webhooks.vue'),
          },
          {
            name: 'sdk',
            path: 'sdk',
            component: () => import('../pages/developer/Sdk.vue'),
          },
          {
            name: 'api-logs',
            path: 'api-logs',
            component: () => import('../pages/developer/ApiLogs.vue'),
          },
          {
            name: 'project',
            path: 'project',
            component: () => import('../pages/developer/Project.vue'),
          },
          {
            name: 'messaging',
            path: 'messaging',
            component: () => import('../pages/developer/Messaging.vue'),
          },
          {
            name: 'presence-guide',
            path: 'presence-guide',
            component: () => import('../pages/developer/PresenceGuide.vue'),
          },
          {
            name: 'streaming-guide',
            path: 'streaming-guide',
            component: () => import('../pages/developer/StreamingGuide.vue'),
          },
          {
            name: 'call-guide',
            path: 'call-guide',
            component: () => import('../pages/developer/CallGuide.vue'),
          },
          {
            name: 'push-guide',
            path: 'push-guide',
            component: () => import('../pages/developer/PushGuide.vue'),
          },
          {
            name: 'iot-core-guide',
            path: 'iot-core-guide',
            component: () => import('../pages/developer/IoTCoreGuide.vue'),
          },
        ],
      },
      {
        name: 'cloud',
        path: 'cloud',
        component: RouteViewComponent,
        children: [
          {
            name: 'cloud-storage',
            path: 'cloud-storage',
            component: () => import('../pages/cloud/CloudStorage.vue'),
          },
          {
            name: 'cloud-functions',
            path: 'cloud-functions',
            component: () => import('../pages/cloud/CloudFunctions.vue'),
          },
          {
            name: 'data-analysis',
            path: 'data-analysis',
            component: () => import('../pages/cloud/DataAnalysis.vue'),
          },
        ],
      },
      {
        name: 'documentation',
        path: 'documentation',
        component: RouteViewComponent,
        children: [
          {
            name: 'api-docs',
            path: 'api-docs',
            component: () => import('../pages/documentation/ApiDocs.vue'),
          },
          {
            name: 'integration-guides',
            path: 'integration-guides',
            component: () => import('../pages/documentation/IntegrationGuides.vue'),
          },
          {
            name: 'tutorials',
            path: 'tutorials',
            component: () => import('../pages/documentation/Tutorials.vue'),
          },
        ],
      },
      {
        name: 'settings',
        path: 'settings',
        component: RouteViewComponent,
        children: [
          {
            name: 'appearance',
            path: 'appearance',
            component: () => import('../pages/settings/Settings.vue'),
          },
          {
            name: 'account',
            path: 'account',
            component: () => import('../pages/settings/Account.vue'),
          },
          {
            name: 'profile',
            path: 'profile',
            component: () => import('../pages/auth/Profile.vue'),
          },
          {
            name: 'security',
            path: 'security',
            component: () => import('../pages/settings/security/Security.vue'),
          },
          {
            name: 'notifications',
            path: 'notifications',
            component: () => import('../pages/settings/notifications/Notifications.vue'),
          },
          // {
          //   name: 'preferences',
          //   path: 'preferences',
          //   component: () => import('../pages/settings/Preferences.vue'),
          // },
        ],
      },
      {
        name: 'projects',
        path: 'projects',
        component: () => import('../pages/projects/ProjectsPage.vue'),
      },
      {
        name: 'payments',
        path: '/payments',
        component: RouteViewComponent,
        children: [
          {
            name: 'billing',
            path: 'billing',
            component: () => import('../pages/billing/BillingPage.vue'),
          },
          {
            name: 'services-subscribution',
            path: 'services',
            component: () => import('../pages/services/ServiceList.vue'),
          },
          {
            name: 'service-details',
            path: 'service-details/:id',
            component: () => import('../pages/services/ServiceDetails.vue'),
          },
          {
            name: 'presence',
            path: 'presence',
            component: () => import('../pages/services/Presence.vue'),
          },
          {
            name: 'streaming',
            path: 'streaming',
            component: () => import('../pages/services/Streaming.vue'),
          },
          {
            name: 'video-call',
            path: 'video-call',
            component: () => import('../pages/services/VideoCall.vue'),
          },
          {
            name: 'voice-call',
            path: 'voice-call',
            component: () => import('../pages/services/VoiceCall.vue'),
          },
          {
            name: 'push-notifications',
            path: 'push-notifications',
            component: () => import('../pages/services/PushNotifications.vue'),
          },
        ],
      },
      {
        name: 'faq',
        path: '/faq',
        component: () => import('../pages/faq/FaqPage.vue'),
      },
    ],
  },
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
      },
      {
        name: 'register',
        path: 'register',
        component: () => import('../pages/auth/Register.vue'),
      },
      {
        name: 'activate-account',
        path: 'activate-account',
        component: () => import('../pages/auth/ActivateAccount.vue'),
      },
      {
        name: 'activation-success',
        path: 'activation-success',
        component: () => import('../pages/auth/ActivationSuccess.vue'),
      },
      {
        name: 'forgot-password',
        path: 'forgot-password',
        component: () => import('../pages/auth/ForgotPassword.vue'),
      },
      {
        name: 'reset-password',
        path: 'reset-password',
        component: () => import('../pages/auth/ResetPassword.vue'),
      },
      {
        name: 'reactivate-account',
        path: 'reactivate-account',
        component: () => import('../pages/auth/ReactivateAccount.vue'),
      },
      {
        name: 'reactivate',
        path: 'reactivate',
        component: () => import('../pages/auth/Reactivate.vue'),
      },
      {
        name: 'signup',
        path: 'signup',
        component: () => import('../pages/auth/Signup.vue'),
      },
      {
        name: 'recover-password',
        path: 'recover-password',
        component: () => import('../pages/auth/RecoverPassword.vue'),
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        component: () => import('../pages/auth/CheckTheEmail.vue'),
      },
      {
        path: '',
        redirect: { name: 'login' },
      },
    ],
  },
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

// Navigation guard to check for authentication tokens
router.beforeEach((to, from, next) => {
  const accessToken = sessionStorage.getItem('access')
  const refreshToken = sessionStorage.getItem('refresh')

  // Define public routes that don't require authentication
  const publicRoutes = [
    '/auth/login',
    '/auth/register',
    '/auth/activate-account',
    '/auth/activation-success',
    '/auth/forgot-password',
    '/auth/reset-password',
    '/auth/reactivate-account',
    '/auth/reactivate',
    '/auth/signup',
    '/auth/recover-password',
    '/auth/recover-password-email',
    '/',
    '/ecommerce',
    '/ecommerce/product',
  ]

  // Check if the route is public
  const isPublicRoute =
    publicRoutes.includes(to.path) || to.path.startsWith('/auth/') || to.path.startsWith('/ecommerce/product/') // Handle all product detail pages

  // If user has tokens and is trying to access auth pages, redirect to dashboard
  if (accessToken && refreshToken && to.path.startsWith('/auth')) {
    next({ name: 'dashboard' })
  }
  // If user doesn't have tokens and is trying to access protected routes, redirect to login
  else if (!accessToken || !refreshToken) {
    if (isPublicRoute) {
      next() // Allow access to public routes including home page
    } else {
      // Only redirect to login for protected routes (dashboard, etc.) or when session expires
      next({
        path: '/auth/login',
        query: { redirect: to.fullPath },
      })
    }
  } else {
    // User has tokens and is accessing a protected route
    next()
  }
})

export default router
